import { onSubscriptionsReconnected, useSubscriptionsConnected } from '@up2rent/graphql';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import PaymentsInvalidationListener from './PaymentsInvalidationListener';
import { usePermissions } from './utils/PermissionsContext';

interface PaymentsInvalidationContextValue {
  isBusActive: boolean;
}

const PaymentsInvalidationContext = createContext<PaymentsInvalidationContextValue>({ isBusActive: false });

interface PaymentsInvalidationProviderProps {
  children: ReactNode;
}

export const PaymentsInvalidationProvider = ({ children }: PaymentsInvalidationProviderProps) => {
  const { financialPaymentsRead } = usePermissions();
  const isBusConnected = useSubscriptionsConnected();
  const [isBusRejected, setBusRejected] = useState<boolean>(false);

  useEffect(() => onSubscriptionsReconnected(() => setBusRejected(false)), []);

  const value = useMemo<PaymentsInvalidationContextValue>(
    () => ({ isBusActive: financialPaymentsRead && isBusConnected && !isBusRejected }),
    [financialPaymentsRead, isBusConnected, isBusRejected]
  );

  return (
    <PaymentsInvalidationContext.Provider value={value}>
      {financialPaymentsRead && <PaymentsInvalidationListener onRejected={() => setBusRejected(true)} />}
      {children}
    </PaymentsInvalidationContext.Provider>
  );
};

export const usePaymentsInvalidation = (): PaymentsInvalidationContextValue => useContext(PaymentsInvalidationContext);
